import { useEffect } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { ProjectManagementMissingLevelRatesDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import RateConfigurationCard from 'modules/project_management/components/ProfitabilityCards/RateConfigurationCard'
import SkeletonLoaderCard from 'modules/project_management/components/ProfitabilityCards/RateConfigurationCard/SkeletonLoaderCard'
import { ProjectManagementRateCreateInput } from 'modules/project_management/pages/ProjectDetailsPage/Employees/RatesSettingsSidepanel/types'

interface LevelRatesListProps {
  rates: ProjectManagementRateCreateInput[]
  missingRateIds: string[]
  onChange: (rates: ProjectManagementRateCreateInput[]) => void
  isJobCatalogTreeEnabled: boolean
  currency: string
}

const LevelRatesList = ({
  missingRateIds,
  rates,
  onChange,
  isJobCatalogTreeEnabled,
  currency,
}: LevelRatesListProps) => {
  const resourceType = isJobCatalogTreeEnabled ? 'JobCatalog::TreeLevel' : 'JobCatalog::Level'

  const { data, loading } = useQuery(ProjectManagementMissingLevelRatesDocument, {
    variables: {
      resourceIds: missingRateIds,
      resourceType,
      legacyJobCatalog: !isJobCatalogTreeEnabled,
    },
    skip: missingRateIds.length === 0,
  })

  useEffect(() => {
    if (!data) return

    const missingJobLevels =
      data.jobCatalog.levelsConnection?.nodes || data.jobCatalog.treeLevelsConnection?.nodes || []

    const defaultRateCosts = data.projectManagement?.defaultRateCostsConnection?.nodes || []

    const newRates = missingJobLevels.map((level) => {
      const costCents =
        defaultRateCosts.find(
          (cost) => cost.resourceId === level.id && cost.resourceType === resourceType
        )?.chargingCostCents || 0

      const ancestorName = 'ancestor' in level ? `${level.ancestor?.name} ` : ''

      return {
        resourceId: level.id,
        resourceType,
        billingRateCents: costCents,
        chargingCostCents: costCents,
        markupPercentageCents: 0,
        currency,
        name: ancestorName + (level.name ?? ''),
      }
    })
    onChange(newRates)
  }, [data])

  return (
    <Box gap="s16" paddingTop="s8" paddingX="s24">
      {loading && missingRateIds.map((id) => <SkeletonLoaderCard key={id} />)}

      {rates.map((rate) => {
        return (
          <RateConfigurationCard
            key={rate.resourceId}
            currency={rate.currency}
            maxCostRate={rate.chargingCostCents / 100}
            title={rate.name}
            initialBillRate={(rate.billingRateCents / 100).toFixed(2)}
            profitMargin={rate.markupPercentageCents / 100}
            onChangeValue={(rateValues: { billRate: string; minProfitMargin: string }) => {
              const billRateCents = Math.round(parseFloat(rateValues.billRate) * 100)
              const markupPercentageCents = Math.round(parseFloat(rateValues.minProfitMargin) * 100)

              const newRates = rates.map((existingRate) =>
                existingRate.resourceId === rate.resourceId
                  ? {
                      ...existingRate,
                      billingRateCents: billRateCents,
                      markupPercentageCents: markupPercentageCents,
                    }
                  : existingRate
              )

              onChange(newRates)
            }}
          />
        )
      })}
    </Box>
  )
}

export default LevelRatesList
